import _slicedToArray from '@babel/runtime-corejs3/helpers/esm/slicedToArray';
import _reduceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/reduce';
import _Object$entries from '@babel/runtime-corejs3/core-js-stable/object/entries';
import _concatInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/concat';

/**
 * Takes a selector function and a map of keys and values and creates a switcher function which will return the value associated with the key returned by the selector.
 *
 * @param selector - A selector function to receive the component props and return a value which will be used as the key for the map
 * @param map - A map of possible values to be returned by the selector and corresponding value to be returned in response
 * @returns A switcher function to select a value from the given map based on the result of the given selector
 * @public
 *
 * @example
 * ```javascript
 * import { css } from 'styled-components`
 * import { selectFromMap, selectFromProps } from '@thisisbud/sc-utils'
 *
 * const styles = css`
 *   color: ${selectFromMap(selectFromProps('colorScheme'), {
 *     light: selectFromTheme('lightTextColor'),
 *     dark: selectFromTheme('darkTextColor'),
 *   })}
 * `
 * ```
 */
function selectFromMap(selector, map) {
  return function (props) {
    var value = selector(props);
    var mappedValue = map[value];

    if (typeof mappedValue === 'function') {
      mappedValue = mappedValue(props);
    }

    return mappedValue;
  };
}

/**
 * Creates a selector function to select a given property from the component props.
 * This selector function can then be used in Styled Components template strings to interpolate values.
 *
 * @param key - The property to be selected from the component props
 * @returns A selector function which selects the given key from the component props
 * @public
 *
 * @example
 * ```javascript
 * import { css } from 'styled-components`
 * import { selectFromProps } from '@thisisbud/sc-utils'
 *
 * const styles = css`
 *   color: ${selectFromProps('textColor')}
 * `
 * ```
 */
function selectFromProps(key) {
  return function (props) {
    return props[key]; // eslint-disable-line @typescript-eslint/no-unsafe-return
  };
}

/**
 * Creates a selector function to select a given property from the Styled Components theme in use.
 * This selector function can then be used in Styled Components template strings to interpolate values.
 *
 * @param key - The property to be selected from the Styled Components theme
 * @returns A selector function which selects the given key from the current Styled Components theme
 * @public
 *
 * @example
 * ```javascript
 * import { css } from 'styled-components`
 * import { selectFromTheme } from '@thisisbud/sc-utils'
 *
 * const styles = css`
 *   color: ${selectFromTheme('textColor')}
 * `
 * ```
 */
function selectFromTheme(key) {
  return function (props) {
    return props.theme[key]; // eslint-disable-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
  };
}

/**
 * Creates a CSS string to be used when interpolating styles in Styled Components.
 *
 * @param map - A map of CSS properties and corresponding values or selectors
 * @param props - The props of the Styled Component
 * @returns The resulting CSS string
 */
function mapStyles(map, props) {
  var _context;

  return _reduceInstanceProperty(_context = _Object$entries(map)).call(_context, function (output, _ref) {
    var _context2, _context3;

    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];

    var computedValue = typeof value === 'function' ? value(props) : value;

    if (computedValue === null || typeof computedValue === 'undefined') {
      return output;
    }

    return _concatInstanceProperty(_context2 = _concatInstanceProperty(_context3 = "".concat(output)).call(_context3, key, ":")).call(_context2, computedValue, ";");
  }, '');
}
/**
 * Takes a map of CSS properties and corresponding values or selectors and creates a style mapping function.
 * The style mapping function returned will take the props of a Styled Component and output valid CSS without empty properties.
 *
 * @param map - A map of CSS properties and corresponding values or selectors
 * @returns A style mapping function to generate valid CSS from the props and given values
 * @public
 *
 * @example
 * ```javascript
 * import { css } from 'styled-components`
 * import { selectFromTheme, stylesFromMap } from '@thisisbud/sc-utils'
 *
 * const styles = css`
 *   ${stylesFromMap({
 *     color: selectFromTheme('textColor'),
 *     'font-size': '16px',
 *   })}
 * `
 * ```
 */


function stylesFromMap(map) {
  return function (props) {
    return mapStyles(map, props);
  };
}

export { selectFromMap, selectFromProps, selectFromTheme, stylesFromMap };
